import { z } from "zod";
import { resolveModel } from "@graftus/ai-models";

/**
 * The vault — internal documents a business uploads so the AI coach understands
 * how they actually work (playbooks, pricing, policies, process docs).
 *
 * Firestore layout:
 *   Businesses/{businessId}/documents/{documentId}
 *   Businesses/{businessId}/documents/{documentId}/chunks/{chunkId}
 *
 * Chunks live in a subcollection rather than on the parent: embeddings are
 * ~3k floats each, and Firestore caps a document at 1 MiB.
 */

// ---------------------------------------------------------------------------
// Ingest limits
// ---------------------------------------------------------------------------

/**
 * Indexing runs as a Trigger.dev task, so the pipeline is no longer bounded by
 * a request timeout. The caps remain because cost and latency still are: text
 * extraction is deterministic and fast, and the bound that matters is the
 * embedding batch, hence the chunk cap.
 */
export const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024;
export const MAX_DOCUMENT_CHUNKS = 400;

/** Target chunk size in characters, with overlap so a sentence spanning a
 * boundary is still retrievable from at least one side. */
export const DOCUMENT_CHUNK_SIZE = 1000;
export const DOCUMENT_CHUNK_OVERLAP = 150;

export const DOCX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export const SUPPORTED_DOCUMENT_MIME_TYPES = [
  "application/pdf",
  "text/plain",
  "text/markdown",
  DOCX_MIME_TYPE,
] as const;

/**
 * How much of a document's extracted text is fed to the digest model.
 *
 * A digest describes what the document *covers*, not what it says — the
 * opening pages carry that almost entirely (title page, table of contents,
 * introduction, first sections). Reading the whole thing would multiply the
 * cost of every upload for very little extra signal.
 */
export const MAX_DIGEST_SOURCE_CHARS = 12_000;

/** Topics are index terms, not prose — bounded so the vault block stays compact. */
export const MAX_DOCUMENT_TOPICS = 8;

/**
 * A digest is 1–3 sentences. The cap keeps the vault block in the system
 * prompt bounded by the document count rather than by how verbose the model
 * was on any one file.
 */
export const MAX_DIGEST_SUMMARY_CHARS = 600;

/**
 * How much a perfect keyword match may lift a chunk's rank.
 *
 * Embeddings rank by what a passage is *about*, which is the wrong question for
 * the terms a vault is full of — a bonus model's name, a product code, a policy
 * heading, a year. Those are near-invisible to a dense vector (they carry
 * little topical meaning) yet they are exactly what the user typed. The lexical
 * side is a tie-breaker, not a second opinion: capped low enough that it
 * reorders chunks that are already about the same thing, and never enough to
 * put a keyword-stuffed paragraph above a semantically better one.
 */
export const LEXICAL_RANK_WEIGHT = 0.15;

/**
 * Share of the query's rare-term weight a chunk must contain to be *rescued* —
 * quoted despite scoring under {@link MIN_DOCUMENT_SCORE}.
 *
 * A short question about a named thing ("vad säger Bonusmodell 2026 om
 * provision?") embeds close to nothing: the sentence is mostly function words,
 * and the one term that matters is a proper noun the embedding model has never
 * seen. The paragraph that literally defines it can sit just below the floor.
 * Requiring most of the query's idf mass to be present keeps the rescue narrow
 * enough that it cannot fire on an incidental word overlap.
 */
export const MIN_LEXICAL_RESCUE_SCORE = 0.6;

/**
 * Cosine a rescued chunk must still clear. The lexical signal says the words
 * are there; this says the passage is at least in the same neighbourhood, so a
 * glossary listing every product name can't answer every product question.
 */
export const MIN_RESCUE_MATCH_SCORE = 0.35;

/**
 * Character cap on a single retrieved passage. Passages are stitched from
 * adjacent chunks, so without a cap one long run could crowd out every other
 * document in the same answer.
 */
export const MAX_DOCUMENT_PASSAGE_CHARS = 1400;

/** Shortest suffix/prefix match that counts as a real chunk overlap. */
const MIN_STITCH_OVERLAP = 10;

/**
 * Characters of a document handed back per read.
 *
 * Reading is for questions retrieval answers badly — "summarise the playbook",
 * "what are all the steps" — where the answer is the document's structure
 * rather than the paragraph that best matches a phrase. That means whole text,
 * which for a long handbook is far more than belongs in one tool result, so it
 * comes back in parts the reader can walk through.
 */
export const MAX_DOCUMENT_READ_CHARS = 6000;

/** Extensions accepted by the upload control; mime types from browsers are unreliable. */
export const SUPPORTED_DOCUMENT_EXTENSIONS = [".pdf", ".txt", ".md", ".docx"] as const;

export function isSupportedDocumentFile(
  fileName: string,
  mimeType: string,
): boolean {
  const name = fileName.toLowerCase();
  if (SUPPORTED_DOCUMENT_EXTENSIONS.some((ext) => name.endsWith(ext))) {
    return true;
  }
  const mt = mimeType.toLowerCase();
  // Legacy `.doc` (application/msword) is a different, binary format that
  // mammoth cannot read — it stays rejected by both branches.
  return mt.includes("pdf") || mt.startsWith("text/") || mt === DOCX_MIME_TYPE;
}

// ---------------------------------------------------------------------------
// Embedding space
// ---------------------------------------------------------------------------

/**
 * The embedding model every vault vector is produced with, and its stored
 * width. Both come from the model registry (`packages/ai-models/policy.json`,
 * task `embeddings.default`) and are re-exported here so every reader of the
 * vault agrees on them: the insights and job-ad grounding passes embed their
 * own retrieval queries, and pulling the PDF ingest package in just to learn a
 * model name would be the wrong dependency. Vectors from two different models
 * are not comparable, so a second copy of either value drifting out of sync
 * would silently degrade retrieval rather than fail.
 *
 * `gemini-embedding-001` is what the v1beta `embedContent` endpoint accepts;
 * the older `text-embedding-004` returns 404 against v1beta, which is what the
 * AI SDK Google provider hits. Do not "upgrade" it without checking that
 * endpoint. Firestore's vector indexes cap at 2048 dimensions, so the model's
 * native 3072 cannot be indexed; 1536 halves memory and storage against it
 * while keeping far more retrieval quality than 768.
 *
 * Changing either value invalidates every stored embedding (see
 * `embeddingSignatureKey`) and is its own re-indexing project, not a config
 * change.
 */
const DOCUMENT_EMBEDDING = resolveModel("embeddings.default");
if (DOCUMENT_EMBEDDING.dimensions === undefined) {
  throw new Error("ai-models policy: embeddings.default must declare dimensions");
}

export const DOCUMENT_EMBEDDING_MODEL_ID: string = DOCUMENT_EMBEDDING.modelId;

/** Stored vector width. */
export const EMBEDDING_DIMENSIONS: number = DOCUMENT_EMBEDDING.dimensions;

/**
 * Documents and queries are embedded for *different* roles in the same space.
 * Using the asymmetric task types is the documented usage and measurably
 * improves retrieval; the document-side one is part of the stored signature
 * because it changes the vectors, the query-side one is not.
 */
export const EMBEDDING_DOC_TASK_TYPE = "RETRIEVAL_DOCUMENT";
export const EMBEDDING_QUERY_TASK_TYPE = "RETRIEVAL_QUERY";

/**
 * Provider options for embedding a search query.
 *
 * Alongside the model id because every reader that retrieves — chat, insights
 * grounding, job-ad grounding — has to embed its query exactly the way the
 * stored vectors were produced, minus the task type. The document-side
 * counterpart stays in `@graftus/documents`: only ingest ever writes.
 */
export const queryEmbedOptions = {
  google: {
    outputDimensionality: EMBEDDING_DIMENSIONS,
    taskType: EMBEDDING_QUERY_TASK_TYPE,
  },
} as const;

export interface EmbeddingSignature {
  model: string;
  dimensions: number;
  taskType: string;
}

/**
 * Identifies the embedding space a stored vector belongs to.
 *
 * Retrieval prefilters on this, so a chunk embedded with another model, width
 * or task type is unreachable rather than silently compared — vectors from two
 * spaces are not comparable, and cosine similarity over them returns confident
 * nonsense.
 */
export function embeddingSignatureKey(signature: EmbeddingSignature): string {
  return `${signature.model}:${signature.dimensions}:${signature.taskType}`;
}

/**
 * The space chunks are written into, and the one every reader prefilters on.
 *
 * Derived here rather than in `@graftus/documents` so that retrieval — which
 * has to name the signature on every query — does not have to depend on the
 * ingest package to do it. Ingest re-exports this.
 */
export const CURRENT_EMBEDDING_SIGNATURE = embeddingSignatureKey({
  model: DOCUMENT_EMBEDDING_MODEL_ID,
  dimensions: EMBEDDING_DIMENSIONS,
  taskType: EMBEDDING_DOC_TASK_TYPE,
});

/**
 * Truncated Matryoshka output is not unit-norm (only the full 3072 width is),
 * so normalize before storing and before querying. A zero vector is returned
 * unchanged rather than turned into NaNs.
 */
export function normalizeVector(values: number[]): number[] {
  let sum = 0;
  for (const v of values) sum += v * v;
  const magnitude = Math.sqrt(sum);
  if (magnitude === 0 || !Number.isFinite(magnitude)) return values;
  return values.map((v) => v / magnitude);
}

// ---------------------------------------------------------------------------
// Record
// ---------------------------------------------------------------------------

export const DOCUMENT_STATUSES = ["pending", "indexed", "failed"] as const;
export type DocumentStatus = (typeof DOCUMENT_STATUSES)[number];

/**
 * Who the AI may answer from this document for.
 * - `business` — every member; the company handbook case.
 * - `admins`   — owners and admins only; pricing, margins, sensitive policy.
 */
export const DOCUMENT_VISIBILITIES = ["business", "admins"] as const;
export type DocumentVisibility = (typeof DOCUMENT_VISIBILITIES)[number];

export const DocumentVisibilitySchema = z.enum(DOCUMENT_VISIBILITIES);

/**
 * Which extraction path produced the text. Kept so a document that came back
 * thin can be diagnosed later — `vision` in particular can be truncated by the
 * model's output cap on a long scan.
 */
export const DOCUMENT_EXTRACTION_METHODS = [
  "pdf_text",
  "plain_text",
  "docx",
  "vision",
] as const;
export type DocumentExtractionMethod =
  (typeof DOCUMENT_EXTRACTION_METHODS)[number];

/** Machine-readable `error` values on a failed document, for i18n in the UI. */
export const DOCUMENT_ERROR_CODES = [
  "no_text_layer",
  /**
   * Text came back, but too little or too garbled to index — a scan with only
   * running headers, a broken font encoding, a near-empty note. Distinct from
   * `no_text_layer`: the file is not blank, it is unusable, and the fix is a
   * different export rather than a different file. See `assessExtractedText`.
   */
  "unreadable_text",
  "unsupported_type",
  "too_large",
  "object_missing",
  "extraction_failed",
  "embedding_failed",
] as const;
export type DocumentErrorCode = (typeof DOCUMENT_ERROR_CODES)[number];

/**
 * A document's digest — what it covers, in the model's own words.
 *
 * Retrieval returns chunks, which say nothing about the document as a whole.
 * The digest is what lets the coach know a vault document *exists* and is
 * worth searching before it has searched anything, and it gives the person who
 * uploaded a file a way to check the AI actually read it (a scan that came back
 * as three lines of noise produces an obviously wrong summary).
 */
export const DocumentDigest = z.object({
  summary: z
    .string()
    .min(1)
    .max(MAX_DIGEST_SUMMARY_CHARS)
    .describe(
      "1–3 meningar på svenska: vad dokumentet innehåller och när coachen har nytta av det.",
    ),
  topics: z
    .array(z.string().min(1).max(60))
    .max(MAX_DOCUMENT_TOPICS)
    .describe("Korta ämnesord som beskriver dokumentets innehåll."),
});
export type DocumentDigest = z.infer<typeof DocumentDigest>;

/**
 * Which slice of the org a document is about.
 *
 * Orthogonal to {@link DocumentVisibility}, exactly as insights and actions
 * model it: `visibility` answers "who may be answered from this", `scope`
 * answers "what is it about". A team playbook is `scope: "team"` and still
 * `visibility: "business"`.
 */
export const DOCUMENT_SCOPES = ["business", "team"] as const;
export type DocumentScope = (typeof DOCUMENT_SCOPES)[number];
export const DocumentScopeSchema = z.enum(DOCUMENT_SCOPES);

export interface BusinessDocumentRecord {
  id: string;
  businessId: string;
  /** Human-facing name; defaults to the file name at upload. */
  title: string;
  fileName: string;
  storagePath: string;
  mimeType: string;
  sizeBytes: number;
  visibility: DocumentVisibility;
  scope: DocumentScope;
  /** Set when `scope === "team"`, else null. */
  salesTeamId: string | null;
  status: DocumentStatus;
  extractionMethod: DocumentExtractionMethod | null;
  chunkCount: number;
  /**
   * AI-written description of what this document covers. Null when the
   * document has not been indexed yet, or when the digest call failed — a
   * missing digest never blocks indexing, it only makes the document quieter.
   */
  summary: string | null;
  /** Short index terms from the digest; empty when there is no digest. */
  topics: string[];
  /** A `DocumentErrorCode` when `status === "failed"`, else null. */
  error: string | null;
  /** Embedding space of this document's chunks; null before it was indexed. */
  embeddingSignature: string | null;
  /** sha256 of the uploaded bytes; null for documents indexed before hashing. */
  contentHash: string | null;
  /** Another document with identical bytes, if one existed at index time. */
  duplicateOfDocumentId: string | null;
  uploadedBy: string;
  uploadedAt: string | null;
  indexedAt: string | null;
}

/** True when this document's chunks are not in the current embedding space. */
export function documentNeedsReindex(
  document: Pick<BusinessDocumentRecord, "status" | "embeddingSignature">,
  currentSignature: string,
): boolean {
  if (document.status !== "indexed") return false;
  return document.embeddingSignature !== currentSignature;
}

export interface DocumentChunkRecord {
  id: string;
  index: number;
  text: string;
  embedding: number[];
}

/** A stretch of a document worth quoting — one chunk, or several stitched. */
export interface DocumentPassage {
  /** Chunk index the passage starts at; the run is contiguous from here. */
  chunkIndex: number;
  /** How many chunks were stitched into it. */
  chunkCount: number;
  text: string;
  /** Best score among the stitched chunks. */
  score: number;
}

/**
 * Retrieval, grouped the way a reader thinks about it: per document, with the
 * document's own digest alongside the passages.
 *
 * Chunks are an implementation detail of the index, not a unit of meaning — a
 * model handed five loose excerpts from the same handbook has to reconstruct
 * that they belong together before it can answer, and tends to cite them as if
 * they were five sources.
 */
export interface DocumentSearchMatch {
  documentId: string;
  documentTitle: string;
  summary: string | null;
  topics: string[];
  /** Best passage score; matches are ordered by it. */
  score: number;
  passages: DocumentPassage[];
}

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/**
 * Splits text into overlapping chunks.
 *
 * Prefers to break on a paragraph or sentence boundary near the target size so
 * a chunk reads as coherent prose — an embedding of half a sentence retrieves
 * poorly. Falls back to a hard cut when no boundary is close enough.
 */
export function chunkDocumentText(
  text: string,
  opts: { size?: number; overlap?: number; maxChunks?: number } = {},
): string[] {
  const size = opts.size ?? DOCUMENT_CHUNK_SIZE;
  const overlap = opts.overlap ?? DOCUMENT_CHUNK_OVERLAP;
  const maxChunks = opts.maxChunks ?? MAX_DOCUMENT_CHUNKS;

  const normalized = text.replace(/\r\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
  if (normalized.length === 0) return [];
  if (normalized.length <= size) return [normalized];

  const chunks: string[] = [];
  let start = 0;

  while (start < normalized.length && chunks.length < maxChunks) {
    let end = Math.min(start + size, normalized.length);

    if (end < normalized.length) {
      // Look for a boundary in the last quarter of the window.
      const windowStart = start + Math.floor(size * 0.75);
      const slice = normalized.slice(windowStart, end);
      const paragraph = slice.lastIndexOf("\n\n");
      const sentence = Math.max(
        slice.lastIndexOf(". "),
        slice.lastIndexOf(".\n"),
      );
      if (paragraph >= 0) {
        end = windowStart + paragraph + 2;
      } else if (sentence >= 0) {
        end = windowStart + sentence + 1;
      }
    }

    const chunk = normalized.slice(start, end).trim();
    if (chunk.length > 0) chunks.push(chunk);

    if (end >= normalized.length) break;
    // Step back by the overlap, but always make forward progress.
    start = Math.max(end - overlap, start + 1);
  }

  return chunks;
}

/**
 * Joins two consecutive chunks back into continuous text.
 *
 * `chunkDocumentText` overlaps neighbours by `DOCUMENT_CHUNK_OVERLAP`
 * characters, so concatenating them naively repeats a paragraph — which reads
 * to a model like the document says the same thing twice. Finds the longest
 * suffix of `a` that opens `b` and drops it from `b`. Chunks are trimmed at
 * both ends, so the overlap can be a few characters shorter than nominal;
 * the search window allows for that. Falls back to a newline join when no
 * overlap is found, which is what happens when the two chunks aren't actually
 * neighbours.
 */
export function stitchChunkText(a: string, b: string): string {
  const window = Math.min(DOCUMENT_CHUNK_OVERLAP + 40, a.length, b.length);
  // Stop well before a one-character "overlap": neighbours share ~150
  // characters, so anything this short is a coincidence, and trusting it would
  // clip a letter off the second chunk.
  for (let k = window; k >= MIN_STITCH_OVERLAP; k--) {
    if (a.endsWith(b.slice(0, k))) return a + b.slice(k);
  }
  return `${a}\n${b}`;
}

/**
 * Collapses scored chunks of one document into passages, stitching runs of
 * adjacent chunks and keeping the strongest score of each run.
 *
 * Input order does not matter; output is strongest passage first.
 */
export function assemblePassages<
  T extends { chunkIndex: number; text: string; score: number },
>(hits: T[], opts: { maxChars?: number } = {}): DocumentPassage[] {
  const maxChars = opts.maxChars ?? MAX_DOCUMENT_PASSAGE_CHARS;
  const ordered = [...hits].sort((x, y) => x.chunkIndex - y.chunkIndex);
  const passages: DocumentPassage[] = [];

  for (const hit of ordered) {
    const open = passages[passages.length - 1];
    const isNeighbour =
      open != null && hit.chunkIndex === open.chunkIndex + open.chunkCount;
    if (open && isNeighbour) {
      open.text = stitchChunkText(open.text, hit.text);
      open.chunkCount += 1;
      open.score = Math.max(open.score, hit.score);
    } else {
      passages.push({
        chunkIndex: hit.chunkIndex,
        chunkCount: 1,
        text: hit.text,
        score: hit.score,
      });
    }
  }

  return passages
    .map((p) => ({ ...p, text: p.text.slice(0, maxChars).trim() }))
    .sort((x, y) => y.score - x.score);
}

/**
 * Rebuilds a document's continuous text from its chunks.
 *
 * Chunks are assumed to be one document's, in any order; they are sorted by
 * index and stitched pairwise so the ingest overlap is removed exactly. A
 * missing chunk (index gap) falls back to a newline join inside
 * `stitchChunkText`, which is the honest rendering — the text really is
 * discontinuous there.
 */
export function joinDocumentChunks(
  chunks: Array<{ index: number; text: string }>,
): string {
  const ordered = [...chunks]
    .sort((a, b) => a.index - b.index)
    .map((c) => c.text)
    .filter((t) => t.length > 0);
  if (ordered.length === 0) return "";
  return ordered.reduce((acc, next) => stitchChunkText(acc, next));
}

/**
 * Splits a document's text into readable parts.
 *
 * Reuses the ingest chunker with zero overlap: it already prefers paragraph and
 * sentence boundaries, so a part ends where the document does rather than
 * mid-word. Empty text yields one empty part, so `partCount` is never zero and
 * a caller never has to special-case "there is nothing to read".
 */
export function splitDocumentIntoParts(
  text: string,
  opts: { partChars?: number } = {},
): string[] {
  const partChars = opts.partChars ?? MAX_DOCUMENT_READ_CHARS;
  const parts = chunkDocumentText(text, { size: partChars, overlap: 0 });
  return parts.length > 0 ? parts : [""];
}

/** Trailing file extension carried into a title by the upload default. */
const TITLE_EXTENSION_RE = /\.(pdf|txt|md)$/;

function normalizeDocumentName(value: string): string {
  return value
    .trim()
    .replace(/^["'«»]+|["'«»]+$/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(TITLE_EXTENSION_RE, "")
    .trim();
}

/**
 * Resolves a document the model named back to a real document.
 *
 * The name comes from the vault index in the system prompt or from a search
 * result, so it is usually exact — but models drop file extensions, change
 * casing and quote titles. Matching is therefore normalised, and falls back to
 * containment in either direction ("säljmanual" → "Säljmanual 2026").
 *
 * Returns null when nothing matches *or* when several documents match equally
 * well: picking one of two plausible handbooks silently would answer from the
 * wrong document, while null lets the caller hand back the real titles and let
 * the model choose.
 */
export function matchDocumentByName<T extends { id: string; title: string }>(
  documents: T[],
  query: string,
): T | null {
  const raw = query.trim();
  if (raw.length === 0) return null;

  const byId = documents.find((d) => d.id === raw);
  if (byId) return byId;

  const needle = normalizeDocumentName(raw);
  if (needle.length === 0) return null;

  const exact = documents.filter((d) => normalizeDocumentName(d.title) === needle);
  if (exact.length === 1) return exact[0] ?? null;
  if (exact.length > 1) return null;

  const partial = documents.filter((d) => {
    const title = normalizeDocumentName(d.title);
    return title.includes(needle) || needle.includes(title);
  });
  return partial.length === 1 ? (partial[0] ?? null) : null;
}

// ---------------------------------------------------------------------------
// Lexical matching
// ---------------------------------------------------------------------------

/**
 * Words carrying no retrieval signal. Swedish, plus the English that shows up
 * in Swedish business documents. Kept deliberately short — a stopword list that
 * strays into domain words ("mål", "kund") starts deleting the query.
 */
const LEXICAL_STOPWORDS = new Set([
  "och", "att", "det", "som", "för", "med", "den", "till", "har", "de",
  "inte", "om", "vi", "ett", "en", "är", "på", "av", "kan", "man",
  "vad", "hur", "när", "var", "vem", "vilka", "vilken", "vilket", "ska",
  "skall", "vara", "blir", "från", "eller", "men", "också", "sig", "här",
  "där", "under", "över", "efter", "mellan", "samt", "detta", "denna",
  "dessa", "sin", "sina", "sitt", "deras", "våra", "vår", "vårt", "ni",
  "the", "and", "for", "with", "this", "that", "from", "are", "our",
]);

/** Query and chunk tokens have to survive this to be worth comparing. */
const MIN_LEXICAL_TOKEN_LENGTH = 2;

/**
 * Share of the corpus a term may appear in and still count.
 *
 * A word in a quarter of a vault's chunks cannot tell one chunk from another,
 * whatever a general stopword list thinks of it — in a vault of sales
 * handbooks that is "kund" and "provision". This is the corpus-relative half of
 * the stopword rule, and the one that actually holds the rescue narrow.
 */
const MAX_LEXICAL_DOCUMENT_FREQUENCY = 0.25;

/**
 * Splits text into comparable terms: lowercased, punctuation dropped, stopwords
 * removed. Deliberately not stemmed — Swedish compounding means a stemmer would
 * have to be a real one, and an exact match on "provisionstrappan" is precisely
 * the signal this is here to catch.
 */
export function tokenizeForLexicalMatch(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(
      (t) => t.length >= MIN_LEXICAL_TOKEN_LENGTH && !LEXICAL_STOPWORDS.has(t),
    );
}

/**
 * Tokenized corpus with the inverse document frequency of each term — how rare
 * a word is among the texts that were indexed.
 */
export interface LexicalIndex {
  /** Distinct tokens of each indexed text, in the order the texts were given. */
  documentTokens: Set<string>[];
  /** Weight per *discriminative* term; a term too common to help is absent. */
  idf: Map<string, number>;
  size: number;
}

/**
 * Builds the lexical index for one search, over the texts that search may
 * return.
 *
 * Rarity is measured against *this* corpus rather than a language model of
 * Swedish, which is what makes it useful: in a vault of sales handbooks the
 * word "provision" is common and says little, while it would look rare against
 * any general corpus.
 */
export function buildLexicalIndex(texts: string[]): LexicalIndex {
  const documentTokens = texts.map((t) => new Set(tokenizeForLexicalMatch(t)));
  const frequency = new Map<string, number>();
  for (const tokens of documentTokens) {
    for (const token of tokens) {
      frequency.set(token, (frequency.get(token) ?? 0) + 1);
    }
  }
  const size = documentTokens.length;
  // At least 1, so a term unique to a single chunk always counts however small
  // the vault is. A one-chunk vault is degenerate for this — every word in it
  // is unique — and there the cosine floor is what holds.
  const maxFrequency = Math.max(1, size * MAX_LEXICAL_DOCUMENT_FREQUENCY);
  const idf = new Map<string, number>();
  for (const [token, count] of frequency) {
    if (count > maxFrequency) continue;
    idf.set(token, Math.log((size + 1) / (count + 1)) + 1);
  }
  return { documentTokens, idf, size };
}

/**
 * Share of the query's rarity-weighted terms present in the indexed text at
 * `position`, in 0..1.
 *
 * Weighting by idf is what separates "mentions the thing you asked about" from
 * "happens to share the word process": matching one rare term is worth more
 * than matching three common ones.
 *
 * Only discriminative terms count. A term the corpus has never seen is ignored
 * rather than scored as a miss: no chunk has it, so keeping it would penalise
 * every chunk equally and turn the threshold into a measure of how wordy the
 * question was — "Vad säger provisionstrappan?" has to score the same as
 * "provisionstrappan". A term most of the corpus contains is ignored for the
 * mirror-image reason, and that is what stops a shared filler word from
 * reading as full coverage.
 */
export function lexicalMatchScore(
  queryTokens: string[],
  position: number,
  index: LexicalIndex,
): number {
  const tokens = index.documentTokens[position];
  if (!tokens) return 0;
  let total = 0;
  let matched = 0;
  for (const token of new Set(queryTokens)) {
    const weight = index.idf.get(token);
    if (weight === undefined) continue;
    total += weight;
    if (tokens.has(token)) matched += weight;
  }
  return total > 0 ? matched / total : 0;
}

/** A chunk's standing after both retrieval signals have been taken into account. */
export interface RankedMatch {
  /** Score used for ordering, and reported to whoever reads the passage. */
  score: number;
  /** Whether the chunk is relevant enough to be quoted at all. */
  keep: boolean;
}

/**
 * Combines the semantic and lexical signals into one verdict.
 *
 * Semantic similarity stays the primary judgement — it decides admission on its
 * own, and dominates the ordering. Lexical overlap does two narrower jobs:
 * it nudges rank among chunks that are already about the right thing, and it
 * rescues the near-miss that literally contains what was asked about.
 */
export function rankMatch(semantic: number, lexical: number): RankedMatch {
  const rescued =
    semantic >= MIN_RESCUE_MATCH_SCORE && lexical >= MIN_LEXICAL_RESCUE_SCORE;
  return {
    score: semantic + LEXICAL_RANK_WEIGHT * lexical,
    keep: semantic >= MIN_DOCUMENT_SCORE || rescued,
  };
}

/** Documents a caller with this role may be answered from. */
export function visibleDocumentsForRole<
  T extends { visibility: DocumentVisibility },
>(documents: T[], role: string | undefined): T[] {
  if (role === "owner" || role === "admin") return documents;
  return documents.filter((d) => d.visibility === "business");
}

/**
 * Documents within the caller's team scope. Compose with — never merge into —
 * {@link visibleDocumentsForRole}: the two answer different questions.
 *
 * A team-scoped document with no team is denied, matching how insights treat
 * the same shape: fail closed rather than leak to the whole org.
 */
export function visibleDocumentsForScope<
  T extends { scope: DocumentScope; salesTeamId: string | null },
>(
  documents: T[],
  visibility: { mode: "org" } | { mode: "scoped"; visibleTeamIds: string[] },
): T[] {
  if (visibility.mode === "org") return documents;
  return documents.filter((d) => {
    if (d.scope !== "team") return true;
    return d.salesTeamId != null && visibility.visibleTeamIds.includes(d.salesTeamId);
  });
}

// ---------------------------------------------------------------------------
// Ingest state
// ---------------------------------------------------------------------------

/**
 * A document whose indexing never finished. Indexing is enqueued the moment the
 * bytes land, so anything still `pending` this long after upload lost its job —
 * a closed tab between upload and finalize, or a worker that never ran. Shown
 * as stalled with a re-index button rather than an eternal spinner.
 */
export const DOCUMENT_PENDING_STALE_MS = 10 * 60 * 1000;

export function isDocumentIndexingStale(
  document: Pick<BusinessDocumentRecord, "status" | "uploadedAt">,
  now: number = Date.now(),
): boolean {
  if (document.status !== "pending") return false;
  // No timestamp yet means the record is mid-write, not stuck.
  if (!document.uploadedAt) return false;
  const uploadedAt = Date.parse(document.uploadedAt);
  if (Number.isNaN(uploadedAt)) return false;
  return now - uploadedAt > DOCUMENT_PENDING_STALE_MS;
}

// ---------------------------------------------------------------------------
// Retrieval tuning
// ---------------------------------------------------------------------------

/**
 * Cosine-similarity floor for a hit to be worth showing the model. Without it
 * a question the vault cannot answer still returns the five least-bad chunks,
 * and the model cites them.
 *
 * A false negative just means the reader answers without the vault (the status
 * quo); a false positive puts irrelevant text in front of a model that has been
 * told to trust and cite it. Shared by every reader (chat retrieval, insights
 * grounding, job-ad grounding) so "relevant enough to quote" means one thing
 * across the platform, and calibrated against *this* embedding space — 1536
 * dimensions, normalized, asymmetric task types. A floor measured against any
 * other configuration does not transfer.
 */
export const MIN_DOCUMENT_SCORE = 0.55;

/**
 * Chunks kept from any single document, so one large file cannot crowd out the
 * rest.
 *
 * Higher than a per-chunk reader would need because chunks are stitched into
 * passages before they are returned: four adjacent chunks of the same section
 * collapse into one continuous quote, so the cap bounds how much of a document
 * a single passage may carry rather than how many separate excerpts come back.
 */
export const MAX_CHUNKS_PER_DOCUMENT = 4;

/** Candidates fetched per requested hit, to give the diversity cap room. */
export const DOCUMENT_SEARCH_OVERFETCH = 8;

/**
 * Chunk document id. The zero-padded index is what makes neighbour expansion a
 * point read instead of a query, so the writer and the reader must derive it
 * from this one function.
 */
export function documentChunkId(index: number): string {
  return String(index).padStart(4, "0");
}

/** The chunk itself plus its neighbours, clamped to the document. */
export function neighbourChunkIndices(
  index: number,
  chunkCount: number,
): number[] {
  const indices: number[] = [];
  for (let i = index - 1; i <= index + 1; i++) {
    if (i >= 0 && i < chunkCount) indices.push(i);
  }
  return indices;
}

/**
 * Keeps at most `cap` hits per document, preserving score order.
 *
 * Returning fewer than requested is correct — breadth across documents beats
 * five consecutive chunks of the same handbook.
 */
export function applyDiversityCap<T extends { documentId: string }>(
  hits: T[],
  cap: number = MAX_CHUNKS_PER_DOCUMENT,
): T[] {
  const seen = new Map<string, number>();
  const kept: T[] = [];
  for (const hit of hits) {
    const count = seen.get(hit.documentId) ?? 0;
    if (count >= cap) continue;
    seen.set(hit.documentId, count + 1);
    kept.push(hit);
  }
  return kept;
}

/**
 * Joins consecutive chunks back into readable prose.
 *
 * Chunks overlap by {@link DOCUMENT_CHUNK_OVERLAP} characters, so a naive join
 * repeats a sentence at every seam. Trims the longest suffix/prefix match up to
 * the overlap width.
 */
export function mergeAdjacentChunks(texts: string[]): string {
  if (texts.length === 0) return "";
  let merged = texts[0] ?? "";
  for (const next of texts.slice(1)) {
    const maxSeam = Math.min(DOCUMENT_CHUNK_OVERLAP + 20, merged.length, next.length);
    let seam = 0;
    for (let width = maxSeam; width > 20; width--) {
      if (merged.slice(-width) === next.slice(0, width)) {
        seam = width;
        break;
      }
    }
    merged = `${merged}\n${next.slice(seam)}`.trim();
  }
  return merged;
}

// ---------------------------------------------------------------------------
// Extraction quality
// ---------------------------------------------------------------------------

/**
 * Whether extracted text is worth indexing at all.
 *
 * Everything downstream assumes the text it was handed is the document, and
 * nothing in the chain can tell prose from an artefact of the file: a scan
 * whose only text layer is the running header extracts as thirty page numbers,
 * a PDF with a broken font encoding extracts as letter soup, and both chunk
 * cleanly, embed without complaint, get summarised with confidence and then
 * answer questions in the coach's voice. The one place to catch that is here,
 * before any of it runs.
 *
 * The tests are structural rather than linguistic — is this shaped like words
 * on pages, not is this Swedish — so a price list, an English policy and a
 * table of contents all pass.
 */

/** Below this there is nothing to retrieve from, whatever the file was. */
export const MIN_EXTRACTED_TEXT_CHARS = 60;

/**
 * Text per page below which a PDF is treated as unread rather than short. A
 * page of prose runs to a few thousand characters; a scanned page whose text
 * layer is a header and a page number lands near twenty. Applied from two pages
 * up, so a one-page notice is judged on its total length alone.
 */
export const MIN_EXTRACTED_CHARS_PER_PAGE = 50;

/**
 * Share of non-space characters that must be letters or digits. A failed
 * encoding replaces glyphs with substitution and box characters, which reads as
 * punctuation; real text stays well clear of this even when it is mostly a
 * table.
 */
export const MIN_LEGIBLE_CHARACTER_RATIO = 0.5;

/**
 * Share of word-shaped tokens that must contain a vowel. Words do; a scrambled
 * CID mapping produces consonant runs that do not. Tokens with no letter at all
 * (page numbers, prices, dates) count neither way, so a price list is judged on
 * its prose.
 */
export const MIN_VOWEL_TOKEN_RATIO = 0.5;

/**
 * Mean token length bounds. A PDF that loses its spaces extracts as one
 * enormous token per line; one that splits every glyph extracts as single
 * characters. Ordinary prose sits near five.
 */
export const MIN_MEAN_TOKEN_LENGTH = 2;
export const MAX_MEAN_TOKEN_LENGTH = 30;

/**
 * `empty` — too little text to be a document. `sparse` — text, but far less
 * than the page count implies, which is what an image-only PDF with running
 * headers looks like. `garbled` — enough text, but not shaped like words.
 */
export type ExtractedTextVerdict = "readable" | "empty" | "sparse" | "garbled";

export interface ExtractedTextAssessment {
  verdict: ExtractedTextVerdict;
  charCount: number;
  legibleRatio: number;
  vowelTokenRatio: number;
  meanTokenLength: number;
}

const LETTER_OR_DIGIT = /[\p{L}\p{N}]/u;
const LETTER = /\p{L}/u;
/** Swedish vowels plus the accented forms that turn up in imported text. */
const VOWEL = /[aeiouyåäöàáâãèéêëìíîïòóôõøùúûüýÿæœ]/iu;

/**
 * Structural read on a piece of extracted text. Pure, so the thresholds can be
 * exercised at their boundaries without a PDF.
 */
export function assessExtractedText(
  text: string,
  opts: { pageCount?: number | null } = {},
): ExtractedTextAssessment {
  const trimmed = text.trim();
  const tokens = trimmed.split(/\s+/).filter(Boolean);
  const nonSpace = trimmed.replace(/\s+/g, "");

  let legible = 0;
  for (const ch of nonSpace) {
    if (LETTER_OR_DIGIT.test(ch)) legible += 1;
  }
  const legibleRatio = nonSpace.length > 0 ? legible / nonSpace.length : 0;

  const wordTokens = tokens.filter((t) => LETTER.test(t));
  const vowelTokenRatio =
    wordTokens.length > 0
      ? wordTokens.filter((t) => VOWEL.test(t)).length / wordTokens.length
      : 0;
  const meanTokenLength =
    tokens.length > 0 ? nonSpace.length / tokens.length : 0;

  const metrics = {
    charCount: trimmed.length,
    legibleRatio,
    vowelTokenRatio,
    meanTokenLength,
  };

  if (trimmed.length < MIN_EXTRACTED_TEXT_CHARS) {
    return { verdict: "empty", ...metrics };
  }

  const pageCount = opts.pageCount ?? null;
  if (
    pageCount !== null &&
    pageCount >= 2 &&
    trimmed.length < pageCount * MIN_EXTRACTED_CHARS_PER_PAGE
  ) {
    return { verdict: "sparse", ...metrics };
  }

  if (
    legibleRatio < MIN_LEGIBLE_CHARACTER_RATIO ||
    vowelTokenRatio < MIN_VOWEL_TOKEN_RATIO ||
    meanTokenLength < MIN_MEAN_TOKEN_LENGTH ||
    meanTokenLength > MAX_MEAN_TOKEN_LENGTH
  ) {
    return { verdict: "garbled", ...metrics };
  }

  return { verdict: "readable", ...metrics };
}

// ---------------------------------------------------------------------------
// tRPC inputs
// ---------------------------------------------------------------------------

export const ListDocumentsInput = z.object({
  businessId: z.string().min(1),
});
export type ListDocumentsInput = z.infer<typeof ListDocumentsInput>;

/** `scope: "team"` needs a team, and a business-wide document must not carry one. */
const scopeFields = {
  scope: DocumentScopeSchema.default("business"),
  salesTeamId: z.string().min(1).nullable().default(null),
};

function refineScope(
  value: { scope: DocumentScope; salesTeamId: string | null },
  ctx: z.RefinementCtx,
): void {
  if (value.scope === "team" && !value.salesTeamId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["salesTeamId"],
      message: "A team-scoped document must name a team",
    });
  }
  if (value.scope === "business" && value.salesTeamId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["salesTeamId"],
      message: "A business-scoped document must not name a team",
    });
  }
}

export const CreateDocumentUploadInput = z
  .object({
    businessId: z.string().min(1),
    fileName: z.string().min(1).max(200),
    title: z.string().max(200).optional(),
    mimeType: z.string().min(1).max(200),
    sizeBytes: z.number().int().min(1).max(MAX_DOCUMENT_BYTES),
    visibility: DocumentVisibilitySchema.default("business"),
    ...scopeFields,
  })
  .superRefine(refineScope);
export type CreateDocumentUploadInput = z.infer<
  typeof CreateDocumentUploadInput
>;

export const FinalizeDocumentUploadInput = z.object({
  businessId: z.string().min(1),
  documentId: z.string().min(1),
});
export type FinalizeDocumentUploadInput = z.infer<
  typeof FinalizeDocumentUploadInput
>;

export const DeleteDocumentInput = z.object({
  businessId: z.string().min(1),
  documentId: z.string().min(1),
});
export type DeleteDocumentInput = z.infer<typeof DeleteDocumentInput>;

/**
 * Update is a patch, so scope must be genuinely optional here — the defaults
 * that `CreateDocumentUploadInput` needs would turn a rename into a silent
 * "move this document back to the whole company", and that change fans out to
 * every chunk.
 */
export const UpdateDocumentInput = z
  .object({
    businessId: z.string().min(1),
    documentId: z.string().min(1),
    title: z.string().min(1).max(200).optional(),
    visibility: DocumentVisibilitySchema.optional(),
    scope: DocumentScopeSchema.optional(),
    salesTeamId: z.string().min(1).nullable().optional(),
  })
  .superRefine((value, ctx) => {
    // Only when the caller is actually changing the scope.
    if (value.scope === undefined) return;
    refineScope({ scope: value.scope, salesTeamId: value.salesTeamId ?? null }, ctx);
  });
export type UpdateDocumentInput = z.infer<typeof UpdateDocumentInput>;

export const ReindexDocumentInput = z.object({
  businessId: z.string().min(1),
  documentId: z.string().min(1),
});
export type ReindexDocumentInput = z.infer<typeof ReindexDocumentInput>;

export const ReindexAllDocumentsInput = z.object({
  businessId: z.string().min(1),
});
export type ReindexAllDocumentsInput = z.infer<typeof ReindexAllDocumentsInput>;

export const GetDocumentDownloadUrlInput = z.object({
  businessId: z.string().min(1),
  documentId: z.string().min(1),
});
export type GetDocumentDownloadUrlInput = z.infer<
  typeof GetDocumentDownloadUrlInput
>;
